import { useEffect, useState } from 'react';
import Layout from '../components/Layout';
import { api, parsePaginatedResponse } from '../lib/api';
import { useAuthStore } from '../store/auth';

interface Metrics {
    total_players: number;
    total_sessions: number;
    total_plays: number;
    total_rewards_awarded: number;
    conversion_rate: number;
    unique_players: number;
    plays_by_day?: Record<string, number>;
}

interface CampaignSummary {
    id: string;
    name: string;
    status: string;
    created_at?: string;
}

interface UsageCounter {
    key: string;
    used: number;
    limit: number | null;
    percentage: number;
    status: string;
}

const colorMap: Record<string, string> = {
    blue: 'text-blue-600',
    indigo: 'text-indigo-600',
    green: 'text-green-600',
    purple: 'text-purple-600',
    orange: 'text-orange-600',
    teal: 'text-teal-600',
};

export default function Dashboard() {
    const [metrics, setMetrics] = useState<Metrics | null>(null);
    const [campaigns, setCampaigns] = useState<CampaignSummary[]>([]);
    const [usageCounters, setUsageCounters] = useState<UsageCounter[]>([]);
    const [loading, setLoading] = useState(true);
    const { user } = useAuthStore();
    const tenantId = localStorage.getItem('impersonated_tenant_id') || user?.tenant_id || '';

    useEffect(() => {
        const fetchMetrics = async () => {
            try {
                const data = await api.getReportMetrics(tenantId);
                const campaignData = await api.getCampaigns(tenantId, 1, 100);
                const usageData = await api.getCurrentUsage(tenantId).catch(() => null);
                setMetrics(data);
                const { items } = parsePaginatedResponse<CampaignSummary>(campaignData);
                setCampaigns(items);
                setUsageCounters(usageData?.counters || []);
            } catch (err) {
                console.error('Failed to fetch metrics:', err);
            } finally {
                setLoading(false);
            }
        };
        fetchMetrics();
    }, [tenantId]);

    if (loading) {
        return (
            <Layout>
                <div className="flex items-center justify-center h-64">
                    <div className="text-gray-500">Đang tải...</div>
                </div>
            </Layout>
        );
    }

    const statCards = [
        { label: 'Chiến dịch đang chạy', value: campaigns.filter((c) => c.status === 'active').length, color: 'blue' },
        { label: 'Tổng chiến dịch', value: campaigns.length, color: 'indigo' },
        { label: 'Tổng người chơi', value: metrics?.total_players || 0, color: 'blue' },
        { label: 'Tổng phiên chơi', value: metrics?.total_sessions || 0, color: 'green' },
        { label: 'Tổng lượt chơi', value: metrics?.total_plays || 0, color: 'purple' },
        { label: 'Phần thưởng đã trao', value: metrics?.total_rewards_awarded || 0, color: 'orange' },
        { label: 'Tỷ lệ chuyển đổi', value: `${metrics?.conversion_rate || 0}%`, color: 'teal' },
        { label: 'Người chơi duy nhất', value: metrics?.unique_players || 0, color: 'indigo' },
    ];
    const recentCampaigns = [...campaigns].slice(0, 5);
    const usageByKey = Object.fromEntries(usageCounters.map((counter) => [counter.key, counter]));

    return (
        <Layout>
            <div className="mb-8">
                <h1 className="text-3xl font-bold text-gray-900">Tổng quan</h1>
                <p className="text-gray-500 mt-1">Tổng quan nền tảng game marketing của bạn</p>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
                {statCards.map((stat) => (
                    <div key={stat.label} className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                        <p className="text-sm text-gray-500">{stat.label}</p>
                        <p className={`text-3xl font-bold mt-2 ${colorMap[stat.color] || 'text-gray-900'}`}>
                            {stat.value.toLocaleString()}
                        </p>
                    </div>
                ))}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
                <div className="lg:col-span-2 bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="text-lg font-semibold text-gray-900">Xu hướng lượt chơi</h2>
                        <span className="text-sm text-gray-500">Những ngày gần đây</span>
                    </div>
                    <div className="h-40 flex items-end gap-2">
                        {Object.entries(metrics?.plays_by_day || {}).slice(-14).map(([day, count]) => {
                            const max = Math.max(...Object.values(metrics?.plays_by_day || { day: 1 }), 1);
                            return (
                                <div key={day} className="flex-1 flex flex-col items-center gap-2">
                                    <div
                                        className="w-full rounded-t bg-blue-500"
                                        style={{ height: `${Math.max(8, (count / max) * 128)}px` }}
                                        title={`${day}: ${count} plays`}
                                    />
                                    <span className="text-[10px] text-gray-400">{new Date(day).getDate()}</span>
                                </div>
                            );
                        })}
                        {Object.keys(metrics?.plays_by_day || {}).length === 0 && (
                            <div className="w-full h-full flex items-center justify-center text-sm text-gray-500">Chưa có dữ liệu lượt chơi</div>
                        )}
                    </div>
                </div>

                <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                    <h2 className="text-lg font-semibold text-gray-900 mb-4">Sử dụng</h2>
                    <div className="space-y-4">
                        {['active_campaigns', 'monthly_plays', 'stored_leads'].map((key) => {
                            const counter = usageByKey[key];
                            return (
                                <div key={key}>
                                    <div className="flex justify-between text-sm">
                                        <span className="text-gray-600">{key.replace(/_/g, ' ')}</span>
                                        <span className="font-medium text-gray-900">{counter?.used || 0}{counter?.limit ? ` / ${counter.limit}` : ''}</span>
                                    </div>
                                    <div className="mt-2 h-2 rounded-full bg-gray-100">
                                        <div className="h-2 rounded-full bg-blue-500" style={{ width: `${Math.min(counter?.percentage || 0, 100)}%` }} />
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
                <div className="lg:col-span-2 bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                    <h2 className="text-lg font-semibold text-gray-900 mb-4">Chiến dịch gần đây</h2>
                    <div className="divide-y divide-gray-100">
                        {recentCampaigns.map((campaign) => (
                            <a key={campaign.id} href={`/campaigns/${campaign.id}`} className="flex items-center justify-between py-3 hover:bg-gray-50">
                                <div>
                                    <div className="font-medium text-gray-900">{campaign.name}</div>
                                    <div className="text-xs text-gray-500">{campaign.created_at ? new Date(campaign.created_at).toLocaleDateString() : ''}</div>
                                </div>
                                <span className="text-xs rounded-full bg-gray-100 px-2 py-1 text-gray-700">{campaign.status}</span>
                            </a>
                        ))}
                        {recentCampaigns.length === 0 && <div className="py-6 text-sm text-gray-500">Chưa có chiến dịch nào</div>}
                    </div>
                </div>

                <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                    <h2 className="text-lg font-semibold text-gray-900 mb-4">Gói dịch vụ</h2>
                    <a href="/billing" className="block rounded-lg border border-blue-100 bg-blue-50 p-4 text-blue-700 hover:bg-blue-100">
                        Xem thanh toán, hạn mức và tùy chọn nâng cấp
                    </a>
                </div>
            </div>

            {/* Quick Actions */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <a href="/campaigns" className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
                    <div className="text-2xl mb-2">📣</div>
                    <h3 className="font-semibold text-gray-900">Chiến dịch</h3>
                    <p className="text-sm text-gray-500 mt-1">Quản lý chiến dịch</p>
                </a>
                <a href="/game-templates" className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
                    <div className="text-2xl mb-2">📦</div>
                    <h3 className="font-semibold text-gray-900">Kho trò chơi</h3>
                    <p className="text-sm text-gray-500 mt-1">Duyệt mẫu trò chơi</p>
                </a>
                <a href="/gifts" className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
                    <div className="text-2xl mb-2">🎁</div>
                    <h3 className="font-semibold text-gray-900">Quà tặng</h3>
                    <p className="text-sm text-gray-500 mt-1">Quản lý kho quà</p>
                </a>
                <a href="/reports" className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
                    <div className="text-2xl mb-2">📈</div>
                    <h3 className="font-semibold text-gray-900">Báo cáo</h3>
                    <p className="text-sm text-gray-500 mt-1">Xem báo cáo chi tiết</p>
                </a>
            </div>
        </Layout>
    );
}
